<template>
  <form-wrapper :validator="$root.$v.moneyLaundering">
    <v-container class="px-5">
      <v-row>
        <v-col cols="12">
          <h1 class="mt-0">{{ $t('moneyLaundering.header') }}</h1>
          <p class="mb-0">{{ $t('moneyLaundering.desc-1') }}</p>
          <p>{{ $t('moneyLaundering.desc-2') }}</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <label class="input-label">{{ $t('labels.typicalClaim') }}</label>
              <form-group
                name="typicalClaim"
                :validator="$root.$v.moneyLaundering.typicalClaim"
              >
                <template slot-scope="{ attrs }">
                  <v-select
                    v-model="moneyLaundering.typicalClaim"
                    :items="typicalClaims"
                    item-text="text"
                    item-value="value"
                    v-bind="attrs"
                    @change="$root.updateMoneyLaundering('typicalClaim', moneyLaundering.typicalClaim); $root.updateMoneyLaundering('typicalClaimText', $t('typicalClaim.' + moneyLaundering.typicalClaim))"
                    @input="$root.$v.moneyLaundering.typicalClaim.$touch()"
                  />
                </template>
              </form-group>
            </v-col>
            <v-col cols="12">
              <label class="input-label">{{ $t('labels.capitalClassification') }}</label>
              <v-radio-group
                v-model="moneyLaundering.capitalClassification"
                :error="$root.$v.moneyLaundering.capitalClassification.$error"
                :error-messages="$root.$v.moneyLaundering.capitalClassification.$error && !$root.$v.moneyLaundering.capitalClassification.required ? [$t('errors.required')] : []"
                @change="$root.updateMoneyLaundering('capitalClassification', moneyLaundering.capitalClassification)"
              >
                <v-radio
                  v-for="capitalClassification in capitalClassifications"
                  :key="capitalClassification.value"
                  :label="capitalClassification.label"
                  :value="capitalClassification.value"
                  @change="$root.$v.moneyLaundering.capitalClassification.$touch(); $root.updateMoneyLaundering('capitalClassificationText', capitalClassification.label)"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <label class="input-label">{{ $t('labels.politicallyInfluentialPerson') }}</label>
          <p class="text-caption">
            {{ $t('moneyLaundering.politicallyInfluentialPersonCaption') }}
            <v-tooltip bottom max-width="300px">
              <template v-slot:activator="{ on }">
                <v-icon color="primary" dark v-on="on">fas fa-info-circle</v-icon>
              </template>
              <span>{{ $t('moneyLaundering.politicallyInfluentialPersonTooltip') }}</span>
            </v-tooltip>
          </p>
          <v-radio-group
            v-model="moneyLaundering.politicallyInfluentialPerson"
            :error="$root.$v.moneyLaundering.politicallyInfluentialPerson.$error"
            :error-messages="$root.$v.moneyLaundering.politicallyInfluentialPerson.$error && !$root.$v.moneyLaundering.politicallyInfluentialPerson.required ? [$t('errors.required')] : []"
            @change="$root.updateMoneyLaundering('politicallyInfluentialPerson', moneyLaundering.politicallyInfluentialPerson)"
          >
            <v-radio
              :label="$t('common.no')"
              :value="false"
              @change="$root.$v.moneyLaundering.politicallyInfluentialPerson.$touch()"
            />
            <v-row
              align="baseline"
              class="ma-0"
            >
              <v-radio
                :label="$t('moneyLaundering.politicallyInfluentialPersonStatusLabel')"
                :value="true"
                class="shrink"
                @change="$root.$v.moneyLaundering.politicallyInfluentialPerson.$touch()"
              />
              <form-group name="politicallyInfluentialPersonStatus">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="moneyLaundering.politicallyInfluentialPersonStatus"
                    :disabled="!moneyLaundering.politicallyInfluentialPerson || moneyLaundering.politicallyInfluentialPerson === null"
                    v-bind="attrs"
                    @input="$root.$v.moneyLaundering.politicallyInfluentialPersonStatus.$touch()"
                    @change="$root.updateMoneyLaundering('politicallyInfluentialPersonStatus', moneyLaundering.politicallyInfluentialPersonStatus)"
                  />
                </template>
              </form-group>
            </v-row>
          </v-radio-group>
        </v-col>
        <v-col
          v-if="isCompany"
          cols="12"
        >
          <label class="input-label">{{ $t('labels.hasNotificationObligation') }}</label>
          <p class="text-caption">
            {{ $t('moneyLaundering.hasNotificationObligationCaption') }}
          </p>
          <v-radio-group
            v-model="moneyLaundering.hasNotificationObligation"
            :error="$root.$v.moneyLaundering.hasNotificationObligation.$error"
            :error-messages="$root.$v.moneyLaundering.hasNotificationObligation.$error && !$root.$v.moneyLaundering.hasNotificationObligation.required ? [$t('errors.required')] : []"
            @change="$root.updateMoneyLaundering('hasNotificationObligation', moneyLaundering.hasNotificationObligation)"
          >
            <v-radio
              :label="$t('common.no')"
              :value="false"
              @change="$root.$v.moneyLaundering.hasNotificationObligation.$touch()"
            />
            <v-radio
              :label="$t('common.yes')"
              :value="true"
              @change="$root.$v.moneyLaundering.hasNotificationObligation.$touch()"
            />
          </v-radio-group>
        </v-col>
        <v-col
          v-if="isCompany"
          cols="12"
        >
          <label class="input-label">{{ $t('labels.needsOperatingLicense') }}</label>
          <v-radio-group
            v-model="moneyLaundering.needsOperatingLicense"
            :error="$root.$v.moneyLaundering.needsOperatingLicense.$error"
            :error-messages="$root.$v.moneyLaundering.needsOperatingLicense.$error && !$root.$v.moneyLaundering.needsOperatingLicense.required ? [$t('errors.required')] : []"
            @change="$root.updateMoneyLaundering('needsOperatingLicense', moneyLaundering.needsOperatingLicense)"
          >
            <v-radio
              :label="$t('common.no')"
              :value="false"
              @change="$root.$v.moneyLaundering.needsOperatingLicense.$touch()"
            />
            <v-row
              align="baseline"
              class="ma-0"
            >
              <v-radio
                :label="$t('moneyLaundering.operatingLicenseNameAndAuthorityLabel')"
                :value="true"
                class="shrink"
                @change="$root.$v.moneyLaundering.needsOperatingLicense.$touch()"
              />
              <form-group name="operatingLicenseNameAndAuthority">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="moneyLaundering.operatingLicenseNameAndAuthority"
                    :disabled="!moneyLaundering.needsOperatingLicense || moneyLaundering.needsOperatingLicense === null"
                    v-bind="attrs"
                    @input="$root.$v.moneyLaundering.operatingLicenseNameAndAuthority.$touch()"
                    @change="$root.updateMoneyLaundering('operatingLicenseNameAndAuthority', moneyLaundering.operatingLicenseNameAndAuthority)"
                  />
                </template>
              </form-group>
            </v-row>
          </v-radio-group>
        </v-col>
        <v-col
          v-if="isCompany"
          cols="12"
        >
          <label class="input-label">{{ $t('labels.foreignGroupCompany') }}</label>
          <v-radio-group
            v-model="moneyLaundering.foreignGroupCompany"
            :error="$root.$v.moneyLaundering.foreignGroupCompany.$error"
            :error-messages="$root.$v.moneyLaundering.foreignGroupCompany.$error && !$root.$v.moneyLaundering.foreignGroupCompany.required ? [$t('errors.required')] : []"
            @change="$root.updateMoneyLaundering('foreignGroupCompany', moneyLaundering.foreignGroupCompany)"
          >
            <v-radio
              :label="$t('common.no')"
              :value="0"
              @change="$root.$v.moneyLaundering.foreignGroupCompany.$touch()"
            />
            <v-radio
              :label="$t('moneyLaundering.foreignGroupCompanyAnswer-1')"
              :value="1"
              @change="$root.$v.moneyLaundering.foreignGroupCompany.$touch()"
            />
            <v-row
              align="baseline"
              class="ma-0"
            >
              <v-radio
                :label="$t('moneyLaundering.foreignGroupCompanyAnswer-2')"
                :value="2"
                class="shrink"
                @change="$root.$v.moneyLaundering.foreignGroupCompany.$touch()"
              />
              <form-group name="foreignGroupCompanyPlace">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="moneyLaundering.foreignGroupCompanyPlace"
                    :disabled="moneyLaundering.foreignGroupCompany !== 2"
                    v-bind="attrs"
                    @input="$root.$v.moneyLaundering.foreignGroupCompanyPlace.$touch()"
                    @change="$root.updateMoneyLaundering('foreignGroupCompanyPlace', moneyLaundering.foreignGroupCompanyPlace)"
                  />
                </template>
              </form-group>
            </v-row>
          </v-radio-group>

          <label class="input-label">
            <span>{{ $t('labels.hasBigShareholders') }} </span>
            <v-dialog v-model="shareholderDialog"
                      :fullscreen="$root.dialogSettings.fullscreen"
                      :scrollable="$root.dialogSettings.scrollable"
                      :hide-overlay="$root.dialogSettings.hideoverlay"
                      :transition="$root.dialogSettings.transition"
                      :width="$root.dialogSettings.width"
                      :max-width="$root.dialogSettings.maxwidth">
              <template v-slot:activator="{ on }">
                <v-icon color="primary" dark v-on="on" style="cursor: pointer;">fas fa-info-circle</v-icon>
              </template>
              <v-card>
                <v-card-title primary-title
                              class="text-h5 primary white--text mb-3 justify-space-between">
                  <span>{{ $t('labels.hasPublicStockExchangeInEUCountry') }}</span>
                  <v-icon color="white"
                          @click="shareholderDialog = false">
                    fas fa-times-circle
                  </v-icon>
                </v-card-title>
                <v-card-text v-html="$t('moneyLaundering.bigShareholderTooltip')" />
                <v-divider></v-divider>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary"
                          text
                          @click="shareholderDialog = false">
                    {{ $t('common.close') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </label>

          <v-radio-group v-model="moneyLaundering.hasBigShareholders"
                          :error="$root.$v.moneyLaundering.hasBigShareholders.$error"
                          :error-messages="$root.$v.moneyLaundering.hasBigShareholders.$error && !$root.$v.moneyLaundering.hasBigShareholders.required ? [$t('errors.required')] : []"
                          @change="$root.updateMoneyLaundering('hasBigShareholders', moneyLaundering.hasBigShareholders)">
            <v-radio :label="$t('moneyLaundering.hasBigShareholdersFalse')"
                      :value="false"
                      @change="$root.$v.moneyLaundering.hasBigShareholders.$touch()" />
            <v-radio :label="$t('moneyLaundering.hasBigShareholdersTrue')"
                      :value="true"
                      @change="$root.$v.moneyLaundering.hasBigShareholders.$touch()" />
          </v-radio-group>

          <v-row v-if="moneyLaundering.hasBigShareholders">
            <v-col cols="12">
              <label class="input-label">{{ $t('labels.hasPublicStockExchangeInEUCountry') }}</label>
              <p class="text-caption">
                {{ $t('moneyLaundering.hasPublicStockExchangeInEUCountryCaption') }}
              </p>
              <v-radio-group v-model="moneyLaundering.hasPublicStockExchangeInEUCountry"
                              :error="$root.$v.moneyLaundering.hasPublicStockExchangeInEUCountry.$error"
                              :error-messages="$root.$v.moneyLaundering.hasPublicStockExchangeInEUCountry.$error && !$root.$v.moneyLaundering.hasPublicStockExchangeInEUCountry.required ? [$t('errors.required')] : []"
                              @change="$root.updateMoneyLaundering('hasPublicStockExchangeInEUCountry', moneyLaundering.hasPublicStockExchangeInEUCountry)">
                <v-row align="baseline"
                        class="ma-0">
                  <v-radio :label="$t('moneyLaundering.hasPublicStockExchangeInEUCountryTrue')"
                            :value="true"
                            class="shrink"
                            @change="$root.$v.moneyLaundering.hasPublicStockExchangeInEUCountry.$touch()" />
                  <form-group name="publicStockExchangeInEUCountry">
                    <template slot-scope="{ attrs }">
                      <v-text-field v-model="moneyLaundering.publicStockExchangeInEUCountry"
                                    :disabled="!moneyLaundering.hasPublicStockExchangeInEUCountry || moneyLaundering.hasPublicStockExchangeInEUCountry === null"
                                    v-bind="attrs"
                                    @input="$root.$v.moneyLaundering.publicStockExchangeInEUCountry.$touch()"
                                    @change="$root.updateMoneyLaundering('publicStockExchangeInEUCountry', moneyLaundering.publicStockExchangeInEUCountry)" />
                    </template>
                  </form-group>
                </v-row>
                <v-radio :label="$t('moneyLaundering.hasPublicStockExchangeInEUCountryFalse')"
                          :value="false"
                          @change="$root.$v.moneyLaundering.hasPublicStockExchangeInEUCountry.$touch()" />
              </v-radio-group>
            </v-col>

            <v-col v-if="moneyLaundering.hasPublicStockExchangeInEUCountry === false"
                    cols="12">
              <v-data-table :headers="headers"
                            :items="shareHolders"
                            hide-default-footer
                            class="elevation-1">
                <template v-slot:items="props">
                  <td>{{ props.item.name }}</td>
                  <td>
                    {{ props.item.dateOfBirth | formatDate }}
                  </td>
                  <td>
                    {{ props.item.nationality }}
                  </td>
                  <td>
                    {{ props.item.countryOfResidence }}
                  </td>
                  <td>
                    {{ props.item.groundsOfAuthority }}
                  </td>
                  <td>
                    {{ props.item.extentOfAuthority }}
                  </td>
                </template>
                <template v-slot:item.action="{ item }">
                  <v-icon small
                          class="mr-2"
                          @click="editShareholder(item)">
                    fa-edit
                  </v-icon>
                  <v-icon small
                          @click="openDeleteDialog(item)">
                    fa-trash
                  </v-icon>
                </template>
                <template v-slot:footer>
                  <v-col class="text-center"
                          cols="12">
                    <v-btn tile
                            color="primary"
                            @click="editDialog = true">
                      <v-icon small
                              class="mr-3">
                        fa-plus
                      </v-icon>{{ $t('moneyLaundering.addNewShareholder')}}
                    </v-btn>
                  </v-col>
                </template>
              </v-data-table>
              <v-dialog v-model="editDialog"
                        :fullscreen="$root.dialogSettings.fullscreen"
                        :scrollable="$root.dialogSettings.scrollable"
                        :hide-overlay="$root.dialogSettings.hideoverlay"
                        :transition="$root.dialogSettings.transition"
                        :width="$root.dialogSettings.width"
                        :max-width="$root.dialogSettings.maxwidth">
                <v-card>
                  <v-card-title primary-title
                                class="text-h5 primary white--text">
                    {{ formTitle }}
                  </v-card-title>

                  <v-card-text>
                    <v-container>
                      <v-row>
                        <v-col cols="12"
                                sm="6">
                          <label class="input-label">{{ $t('labels.name') }}</label>
                          <v-text-field v-model="editedItem.name"
                                        single-line
                                        outlined
                                        :error="$v.editedItem.name.$error"
                                        :error-messages="$v.editedItem.name.$error && !$v.editedItem.name.required ? [$t('validationMessages.required-lite')] : []"
                                        @input="$v.editedItem.name.$touch()"
                                        @blur="$v.editedItem.name.$touch()" />
                        </v-col>
                        <v-col cols="12"
                                sm="6">
                          <label class="input-label">{{ $t('labels.shareHolderBirthdayMenu') }}</label>
                          <v-dialog ref="birthdayPickerDialog"
                                    v-model="shareHolderBirthdayMenu"
                                    :return-value.sync="editedItem.dateOfBirth"
                                    persistent
                                    lazy
                                    width="290px">
                            <template v-slot:activator="{ on }">
                              <v-text-field :value="editedItem.dateOfBirth | formatDate"
                                            single-line
                                            outlined
                                            append-icon="fa-calendar-alt"
                                            readonly
                                            :error="$v.editedItem.dateOfBirth.$error"
                                            :error-messages="$v.editedItem.dateOfBirth.$error && !$v.editedItem.dateOfBirth.required ? [$t('errors.required')] : []"
                                            v-on="on"
                                            @click:append="bill.billDateModal = true"
                                            @input="$v.editedItem.dateOfBirth.$touch()"
                                            @blur="$v.editedItem.dateOfBirth.$touch()"
                                            @change="$v.editedItem.dateOfBirth.$touch()" />
                            </template>
                            <v-date-picker ref="birthdayPicker"
                                            v-model="editedItem.dateOfBirth"
                                            scrollable
                                            :locale="longLocale"
                                            :max="new Date().toISOString().substr(0, 10)"
                                            min="1920-01-01">
                              <v-spacer />
                              <v-btn text
                                      color="primary"
                                      @click="shareHolderBirthdayMenu = false">
                                {{ $t('common.cancel') }}
                              </v-btn>
                              <v-btn text
                                      color="primary"
                                      @click="$refs.birthdayPickerDialog.save(editedItem.dateOfBirth)">
                                {{ $t('common.save') }}
                              </v-btn>
                            </v-date-picker>
                          </v-dialog>
                        </v-col>
                        <v-col cols="12"
                                sm="6">
                          <label class="input-label">{{ $t('labels.nationality') }}</label>
                          <v-text-field v-model="editedItem.nationality"
                                        single-line
                                        outlined
                                        :error="$v.editedItem.nationality.$error"
                                        :error-messages="$v.editedItem.nationality.$error && !$v.editedItem.nationality.required ? [$t('validationMessages.required-lite')] : []"
                                        @input="$v.editedItem.nationality.$touch()"
                                        @blur="$v.editedItem.nationality.$touch()" />
                        </v-col>
                        <v-col cols="12"
                                sm="6">
                          <label class="input-label">{{ $t('labels.countryOfResidence') }}</label>
                          <v-text-field v-model="editedItem.countryOfResidence"
                                        single-line
                                        outlined
                                        :error="$v.editedItem.countryOfResidence.$error"
                                        :error-messages="$v.editedItem.countryOfResidence.$error && !$v.editedItem.countryOfResidence.required ? [$t('validationMessages.required-lite')] : []"
                                        @input="$v.editedItem.countryOfResidence.$touch()"
                                        @blur="$v.editedItem.countryOfResidence.$touch()" />
                        </v-col>
                        <v-col cols="12"
                                sm="6">
                          <label class="input-label">{{ $t('labels.groundsOfAuthority') }}</label>
                          <v-text-field v-model="editedItem.groundsOfAuthority"
                                        single-line
                                        outlined
                                        :error="$v.editedItem.groundsOfAuthority.$error"
                                        :error-messages="$v.editedItem.groundsOfAuthority.$error && !$v.editedItem.groundsOfAuthority.required ? [$t('validationMessages.required-lite')] : []"
                                        @input="$v.editedItem.groundsOfAuthority.$touch()"
                                        @blur="$v.editedItem.groundsOfAuthority.$touch()" />
                        </v-col>
                        <v-col cols="12"
                                sm="6">
                          <label class="input-label">{{ $t('labels.extentOfAuthority') }}</label>
                          <v-text-field v-model="editedItem.extentOfAuthority"
                                        single-line
                                        outlined
                                        :error="$v.editedItem.extentOfAuthority.$error"
                                        :error-messages="$v.editedItem.extentOfAuthority.$error && !$v.editedItem.extentOfAuthority.required ? [$t('validationMessages.required-lite')] : []"
                                        @input="$v.editedItem.extentOfAuthority.$touch()"
                                        @blur="$v.editedItem.extentOfAuthority.$touch()" />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary"
                            text
                            @click="closeDialog">
                      {{ $t('common.cancel') }}
                    </v-btn>
                    <v-btn color="primary"
                            text
                            @click="saveShareholder">
                      {{ $t('common.save') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
              <v-dialog v-model="deleteDialog"
                        :fullscreen="$root.dialogSettings.fullscreen"
                        :scrollable="$root.dialogSettings.scrollable"
                        :hide-overlay="$root.dialogSettings.hideoverlay"
                        :transition="$root.dialogSettings.transition"
                        :width="$root.dialogSettings.width"
                        :max-width="$root.dialogSettings.maxwidth">
                <v-card>
                  <v-card-title primary-title
                                class="text-h5 primary white--text">
                    {{ $t('moneyLaundering.removeShareholder') }}
                  </v-card-title>

                  <v-card-text>
                    <v-container>
                      <v-row>
                        <v-col cols="12"
                                sm="6">
                          <p v-if="itemToDelete !== null">{{ $t('moneyLaundering.removeShareholderQuestion') }} {{ itemToDelete.name }}</p>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary"
                            text
                            @click="deleteDialog = false">
                      {{ $t('common.no') }}
                    </v-btn>
                    <v-btn color="primary"
                            text
                            @click="deleteShareholder">
                      {{ $t('common.yes') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>
          </v-row>
        </v-col>

        <v-col
          v-if="isCompany"
          cols="12"
        >
          <label class="input-label">{{ $t('labels.companyRepresentativeClaim') }}</label>
          <v-radio-group
            v-model="companyRepresentative.isRepresentative"
            :error="$root.$v.moneyLaundering.companyRepresentative.isRepresentative.$error"
            :error-messages="$root.$v.moneyLaundering.companyRepresentative.isRepresentative.$error && !$root.$v.moneyLaundering.companyRepresentative.isRepresentative.required ? [$t('validationMessages.required-lite')] : []"
            @change="$root.updateCompanyRepresentative('isRepresentative', companyRepresentative.isRepresentative)"
          >
            <v-radio
              :label="$t('labels.isCompanyRepresentative')"
              :value="'companyRepresentative'"
              @change="$root.$v.moneyLaundering.companyRepresentative.isRepresentative.$touch()"
            />
            <v-row
              align="baseline"
              class="ma-0"
            >
              <v-radio
                :label="$t('labels.powerOfAttorneyFile') + ' (jpg, jpeg, png, pdf)'"
                :value="'powerOfAttorney'"
                class="shrink"
                @change="$root.$v.moneyLaundering.companyRepresentative.isRepresentative.$touch()"
              />
              <v-text-field
                v-if="companyRepresentative.isRepresentative === 'powerOfAttorney'"
                v-model="companyRepresentative.fileName"
                :label="$t('labels.selectFile')"
                prepend-icon="fa-paperclip"
                clearable
                @click="$refs.file.click()"
                @click:clear="onRemoveImage"
              />
              <input
                ref="file"
                type="file"
                style="display: none"
                accept="image/jpg, image/jpeg, application/pdf"
                @change="onFilePicked"
              >
            </v-row>
            <v-row
              align="baseline"
              class="ma-0"
            >
              <v-radio
                :label="$t('moneyLaundering.companyRepresentativePosition')"
                :value="'position'"
                class="shrink"
                @change="$root.$v.moneyLaundering.companyRepresentative.isRepresentative.$touch()"
              />
              <form-group name="positionInCompany">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="companyRepresentative.positionInCompany"
                    :disabled="companyRepresentative.isRepresentative !== 'position' || companyRepresentative.isRepresentative === null"
                    v-bind="attrs"
                    @input="$root.$v.moneyLaundering.companyRepresentative.positionInCompany.$touch()"
                    @change="$root.updateCompanyRepresentative('positionInCompany', companyRepresentative.positionInCompany)"
                  />
                </template>
              </form-group>
            </v-row>
          </v-radio-group>
          <v-row>
            <v-col
              v-if="filePreview"
              cols="6"
            >
              <img
                :src="filePreview"
                height="150"
              >
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <p>{{ $t('moneyLaundering.footer') }}</p>
        </v-col>

        <v-col
          class="text-center"
          cols="12"
        >
          <v-btn
            large
            tile
            :disabled="!stepperStatus.moneyLaundering.complete"
            :class="{ primary: stepperStatus.moneyLaundering.complete }"
            to="digital-signing"
          >
            {{ $t('common.continue') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </form-wrapper>
</template>

<script>
  import { required, requiredIf, minLength } from 'vuelidate/lib/validators'

  export default {
    data: () => ({
      capitalClassifications: [
        { label: '0 - 10.000', value: 1 },
        { label: '10.000 - 50.000', value: 2 },
        { label: '50.000 - 100.000', value: 3 },
        { label: '100.000 -', value: 4 }
      ],
      editDialog: false,
      deleteDialog: false,
      itemToDelete: null,
      shareHolderBirthdayMenu: false,
      editedIndex: -1,
      editedItem: {
        name: '',
        dateOfBirth: '',
        nationality: '',
        countryOfResidence: '',
        groundsOfAuthority: '',
        extentOfAuthority: ''
      },
      defaultItem: {
        name: '',
        dateOfBirth: '',
        nationality: '',
        countryOfResidence: '',
        groundsOfAuthority: '',
        extentOfAuthority: ''
      },
      shareholderDialog: false
    }),
    computed: {
      headers() {
        return [
          { text: this.$t('labels.name'), value: 'name', sortable: false },
          { text: this.$t('labels.shareHolderBirthdayMenu'), value: 'dateOfBirth', sortable: false, align: 'right' },
          { text: this.$t('labels.nationality'), value: 'nationality', sortable: false, align: 'right' },
          { text: this.$t('labels.countryOfResidence'), value: 'countryOfResidence', sortable: false, align: 'right' },
          { text: this.$t('labels.groundsOfAuthority'), value: 'groundsOfAuthority', sortable: false, align: 'right' },
          { text: this.$t('labels.extentOfAuthority'), value: 'extentOfAuthority', sortable: false, align: 'right' },
          { text: this.$t('labels.action'), value: 'action', sortable: false }
        ]
      },
      moneyLaundering () {
        return this.$store.getters.moneyLaundering
      },
      shareHolders () {
        this.$v.shareHolders.$touch()
        return this.$store.getters.shareholders
      },
      isCompany () {
        return this.$store.getters.client.isCompany
      },
      formTitle () {
        return this.editedIndex === -1 ? this.$t('moneyLaundering.addNewShareholder') : this.$t('moneyLaundering.editShareholder')
      },
      longLocale () {
        return this.$store.getters.longLocale
      },
      typicalClaims () {
        return [
          {
            value: 1,
            text: this.$t('typicalClaim.1')
          },
          {
            value: 2,
            text: this.$t('typicalClaim.2')
          },
          {
            value: 3,
            text: this.$t('typicalClaim.3')
          },
          {
            value: 4,
            text: this.$t('typicalClaim.4')
          },
          {
            value: 5,
            text: this.$t('typicalClaim.5')
          },
          {
            value: 6,
            text: this.$t('typicalClaim.6')
          },
          {
            value: 7,
            text: this.$t('typicalClaim.7')
          },
          {
            value: 8,
            text: this.$t('typicalClaim.8')
          },
          {
            value: 9,
            text: this.$t('typicalClaim.9')
          },
          {
            value: 10,
            text: this.$t('typicalClaim.10')
          },
          {
            value: 11,
            text: this.$t('typicalClaim.11')
          },
          {
            value: 12,
            text: this.$t('typicalClaim.12')
          },
          {
            value: 13,
            text: this.$t('typicalClaim.13')
          },
          {
            value: 14,
            text: this.$t('typicalClaim.14')
          },
          {
            value: 15,
            text: this.$t('typicalClaim.15')
          },
          {
            value: 16,
            text: this.$t('typicalClaim.16')
          },
          {
            value: 17,
            text: this.$t('typicalClaim.17')
          },
          {
            value: 18,
            text: this.$t('typicalClaim.18')
          },
          {
            value: 19,
            text: this.$t('typicalClaim.19')
          }
        ]
      },
      companyRepresentative: function () {
        return this.$store.getters.companyRepresentative
      },
      filePreview: function () {
        return this.companyRepresentative.file !== null ? this.companyRepresentative.file.indexOf('data:image') !== -1 ? this.companyRepresentative.file : null : null
      },
      stepperStatus () {
        return this.$store.getters.stepperStatus
      }
    },
    watch: {
      editDialog (val) {
        val || this.closeDialog()
      },
      shareHolderBirthdayMenu (val) {
        val && setTimeout(() => (this.$refs.birthdayPicker.activePicker = 'YEAR'))
      },
      deleteDialog (val) {
        if (!val) {
          this.itemToDelete = null
        }
      }
    },
    methods: {
      editShareholder (item) {
        this.editedIndex = this.shareHolders.indexOf(item)
        this.editedItem = Object.assign({}, item)
        this.$v.editedItem.$reset()
        this.editDialog = true
      },
      openDeleteDialog (item) {
        this.itemToDelete = item
        this.deleteDialog = true
      },
      deleteShareholder (item) {
        const index = this.shareHolders.indexOf(this.itemToDelete)
        //const index = this.shareHolders.indexOf(item)
        //confirm('Are you sure you want to delete this item?') && this.$store.dispatch('removeShareHolder', index)
        this.$store.dispatch('removeShareHolder', index)
        this.$root.$v.moneyLaundering.$touch()
        this.$store.commit('updateMoneyLaundering', { value: !this.$root.$v.moneyLaundering.$anyError, prop: 'isValid' })
        this.deleteDialog = false
      },
      closeDialog () {
        this.editDialog = false

        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
          this.$v.editedItem.$reset()
        }, 300)
      },
      saveShareholder () {
        this.$v.editedItem.$touch()
        if (!this.$v.editedItem.$anyError) {
          if (this.editedIndex > -1) {
            this.$store.commit('editShareHolder', { index: this.editedIndex, item: this.editedItem })
          } else {
            this.$store.commit('addShareHolder', this.editedItem)
            this.$v.shareHolders.$touch()
            this.$root.$v.moneyLaundering.$touch()
            this.$store.commit('updateMoneyLaundering', { value: !this.$root.$v.moneyLaundering.$anyError, prop: 'isValid' })
          }
          this.closeDialog()
        } else {
          this.$v.editedItem.$touch()
        }
      },
      onFilePicked (e) {
        const files = e.target.files
        if (files[0] !== undefined) {
          if (files[0].name.lastIndexOf('.') <= 0) {
            return
          }
          const fr = new FileReader()
          fr.readAsDataURL(files[0])
          fr.addEventListener('load', () => {
            this.$root.updateCompanyRepresentative('fileName', files[0].name)
            this.$root.updateCompanyRepresentative('file', fr.result)
          })
        } else {
          this.$root.updateCompanyRepresentative('fileName', '')
          this.$root.updateCompanyRepresentative('file', null)
        }
      },
      onRemoveImage () {
        this.$refs.file.value = ''
        this.$root.updateCompanyRepresentative('fileName', '')
        this.$root.updateCompanyRepresentative('file', null)
      }
    },
    validations () {
      return {
        shareHolders: {
          required: requiredIf(() => {
            return this.isCompany &&
              this.moneyLaundering.hasBigShareholders &&
              !this.moneyLaundering.hasPublicStockExchangeInEUCountry
          }),
          minLength: minLength(1)
        },
        editedItem: {
          name: {
            required
          },
          dateOfBirth: {
            required
          },
          nationality: {
            required
          },
          countryOfResidence: {
            required
          },
          groundsOfAuthority: {
            required
          },
          extentOfAuthority: {
            required
          }
        }
      }
    },
    beforeRouteLeave (to, from, next) {
      if (this.$root.$v.client.moneyLaundering) {
        this.$root.$v.moneyLaundering.$touch()
      }
      next()
    }
  }
</script>
